/**
 * Conditional Logic Utilities
 *
 * Handles question visibility and validation based on conditional logic rules
 */

/**
 * Check if a question should be shown based on current answers
 *
 * @param {Object} question - Question schema with conditional_logic
 * @param {Object} answers - Current form answers
 * @returns {boolean} - True if question should be shown
 */
export const shouldShowQuestion = (question, answers) => {
  if (!question.conditional_logic) {
    return true; // No conditions, always show
  }

  // showIfAny: show if ANY of the listed key/value conditions is true.
  // If showIf is also present, both must pass (AND logic).
  if (question.conditional_logic.showIfAny) {
    const { showIfAny } = question.conditional_logic;
    const anyMatch = Object.entries(showIfAny).some(([key, expectedValues]) => {
      const currentValue = answers[key];
      if (!currentValue) return false;
      return Array.isArray(expectedValues) && expectedValues.includes(currentValue);
    });
    if (!anyMatch) return false;
    // showIfAny passed — if there's also a showIf, fall through to check it
    if (!question.conditional_logic.showIf) return true;
  }

  if (!question.conditional_logic.showIf) {
    return true; // No showIf condition, always show
  }

  const { showIf } = question.conditional_logic;
  const { operator = 'in' } = showIf;

  // Get the dependency question key and expected values
  const dependencyKey = Object.keys(showIf).find(key => key !== 'operator');

  if (!dependencyKey) {
    return true;
  }

  const expectedValues = showIf[dependencyKey];
  const currentValue = answers[dependencyKey];

  if (!currentValue) {
    return false; // Dependency not answered yet
  }

  // Check based on operator
  switch (operator) {
    case 'in':
      // Current value must be in expected values array
      return Array.isArray(expectedValues) && expectedValues.includes(currentValue);

    case 'includes':
      // Current value must include one of the expected values (for categorization)
      if (Array.isArray(expectedValues)) {
        return expectedValues.some(expected =>
          currentValue.toLowerCase().includes(expected.toLowerCase())
        );
      }
      return false;

    case 'equals':
      return currentValue === expectedValues;

    default:
      return true;
  }
};

/**
 * Check if a question is required based on current answers
 *
 * @param {Object} question - Question schema
 * @param {Object} answers - Current form answers
 * @returns {boolean} - True if question is required
 */
export const isQuestionRequired = (question, answers) => {
  // First check if question should be shown
  if (!shouldShowQuestion(question, answers)) {
    return false; // Hidden questions are not required
  }

  // Check base required flag
  if (question.is_required) {
    return true;
  }

  // Check conditional required
  if (question.conditional_logic && question.conditional_logic.requiredIf) {
    const { requiredIf } = question.conditional_logic;
    const { operator = 'in' } = requiredIf;

    const dependencyKey = Object.keys(requiredIf).find(key => key !== 'operator');

    if (!dependencyKey) {
      return question.is_required;
    }

    const expectedValues = requiredIf[dependencyKey];
    const currentValue = answers[dependencyKey];

    if (!currentValue) {
      return question.is_required;
    }

    switch (operator) {
      case 'in':
        return Array.isArray(expectedValues) && expectedValues.includes(currentValue);
      case 'equals':
        return currentValue === expectedValues;
      default:
        return question.is_required;
    }
  }

  return question.is_required;
};

/**
 * Filter questions based on current answers
 *
 * @param {Array} questions - All questions
 * @param {Object} answers - Current form answers
 * @returns {Array} - Filtered questions that should be shown
 */
export const filterVisibleQuestions = (questions, answers) => {
  return questions.filter(question => shouldShowQuestion(question, answers));
};

/**
 * Get all required questions that are currently visible
 *
 * @param {Array} questions - All questions
 * @param {Object} answers - Current form answers
 * @returns {Array} - Required questions
 */
export const getRequiredQuestions = (questions, answers) => {
  const visibleQuestions = filterVisibleQuestions(questions, answers);
  return visibleQuestions.filter(question => isQuestionRequired(question, answers));
};

/**
 * Validate if all required questions are answered
 *
 * @param {Array} questions - All questions
 * @param {Object} answers - Current form answers
 * @returns {Object} - { isValid, missingFields }
 */
export const validateRequiredFields = (questions, answers) => {
  const requiredQuestions = getRequiredQuestions(questions, answers);
  const missingFields = [];

  for (const question of requiredQuestions) {
    const answer = answers[question.question_key];

    if (answer === undefined || answer === null || answer === '') {
      missingFields.push({
        key: question.question_key,
        label: question.question_text,
      });
    }
  }

  return {
    isValid: missingFields.length === 0,
    missingFields,
  };
};

/**
 * Calculate completion percentage
 *
 * @param {Array} questions - All questions
 * @param {Object} answers - Current form answers
 * @returns {number} - Percentage (0-100)
 */
export const calculateCompletionPercentage = (questions, answers) => {
  const visibleQuestions = filterVisibleQuestions(questions, answers);

  if (visibleQuestions.length === 0) {
    return 0;
  }

  const answeredCount = visibleQuestions.filter(question => {
    const answer = answers[question.question_key];
    return answer !== undefined && answer !== null && answer !== '';
  }).length;

  return Math.round((answeredCount / visibleQuestions.length) * 100);
};
